@if (section && activePlanPage) {
  <div
    class="report-container"
    data-testid="compliance-report"
    [attr.data-project-id]="activePlanPage.getArchitecturalPlanId()"
    [attr.data-file-id]="activeFileId()"
    [attr.data-page-number]="activePlanPage.getPageNumber()"
    [attr.data-section-id]="section.sectionId"
    [attr.data-section-title]="section.title"
    [attr.data-status]="section.status"
  >
    <div class="report-header">
      @if (showFileSummaryReturn) {
        <button mat-stroked-button type="button" class="file-summary-return" (click)="returnToFileSummary()">
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          Back to file summary
        </button>
      }
      <div class="page-info" data-testid="compliance-report-page">
        Page {{ activePlanPage.getPageNumber() }}: {{ activePlanPage.getTitle() }}
      </div>
      <h3 class="section-title">{{ section.title }}</h3>
      @if (section.hierarchy && section.hierarchy.length > 0) {
        <div class="section-hierarchy">
          @for (h of section.hierarchy; track h; let last = $last) {
            <!--
              An entry becomes a link only when a canonical URL can actually be built for it. The
              previous implementation always rendered an anchor and fell back to '#', which is a dead
              link indistinguishable from a working one until a reader clicks it.
            -->
            @if (hierarchyUrl(h.id)) {
              <a [href]="hierarchyUrl(h.id)" target="_blank" rel="noopener noreferrer" class="hierarchy-link">{{
                h.title
              }}</a>
            } @else {
              <span>{{ h.title }}</span>
            }
            @if (!last) {
              <span class="hierarchy-separator"> &gt; </span>
            }
          }
        </div>
      }
    </div>
    <!-- DISCLAIMER BANNER -->
    <div class="report-disclaimer-banner">
      <mat-icon>warning</mat-icon>
      <span>This report was generated by AI and may have mistakes. Always review with a human expert</span>
    </div>
    <div class="summary-line" data-testid="compliance-report-summary">
      <span class="compliance-chip" [ngClass]="getComplianceClass(section.status)">
        {{ section.status | complianceRiskLabel }}
      </span>
      @if (section.complianceStatementTldr) {
        <span class="summary-text"> <strong>Summary:</strong> {{ section.complianceStatementTldr }} </span>
      }
    </div>
    @if (section.createdTime) {
      <div class="metadata">
        Created on: {{ section.createdTime | date: 'yyyy/MM/dd HH:mm' }} | Model: {{ section.generatedByModel }}
      </div>
    }
    <mat-card class="evaluation-card">
      <mat-card-header>
        <mat-card-title>
          Evaluation of Page {{ activePlanPage.getPageNumber() }} for Compliance with
          @if (sectionUrl()) {
            <a [href]="sectionUrl()" target="_blank" rel="noopener noreferrer" class="section-link">
              {{ section.hierarchy?.[0]?.title }} {{ chapterInfo ? 'Chapter ' + chapterInfo.number : '' }}, Section
              {{ section.sectionId }}
            </a>
          } @else {
            <span class="section-link section-link--plain">
              {{ section.hierarchy?.[0]?.title }} {{ chapterInfo ? 'Chapter ' + chapterInfo.number : '' }}, Section
              {{ section.sectionId }}
            </span>
          }
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <!--
          The report body. One sanitized document written whole, with a citation component attached
          to each placeholder by CitationBodyDirective. Deliberately NOT a list of HTML fragments:
          producing that list meant slicing the serialized sanitized string, which could cut an
          attribute open and yield a fragment that begins as markup — finding F1 of the PR #1824
          security review. The only string trusted here is the sanitized document itself.
        -->
        <div
          class="statement-content"
          data-testid="compliance-report-statement"
          [appCitationBody]="bodyHtml"
          [citations]="citationsById"
          [markerIndexById]="markerIndexById"
          [activeFileId]="activeFileId()"
          [activePageNumber]="activePageNumber()"
          [reportCode]="reportCode"
          [sectionTitles]="sectionTitles"
        ></div>

        @if (visibleSources.length > 0) {
          <!--
            The sources list, showing only the citations that add something the reader cannot already
            see — see visibleSources(). Inline markers stay unobtrusive in the prose, which means they
            cannot also carry a full label or a destination; this is where a reader gets both, and
            where a printed copy keeps its attribution.
          -->
          <section class="report-sources" aria-labelledby="report-sources-heading">
            <h4 id="report-sources-heading" class="report-sources__heading">Sources</h4>
            <ol class="report-sources__list">
              @for (citation of visibleSources; track citation.id) {
                <li class="report-sources__item">
                  <span class="report-sources__marker" aria-hidden="true">{{ markerFor(citation) }}.</span>
                  <app-evidence-citation-chip
                    [citation]="citation"
                    [markerIndex]="markerFor(citation)"
                    [activeFileId]="activeFileId()"
                    [activePageNumber]="activePageNumber()"
                    display="full"
                  />
                  @if (citation.excerpt) {
                    <span class="report-sources__excerpt">“{{ citation.excerpt }}”</span>
                  }
                  <!--
                    Printed only. On screen the destination is the link itself; on paper a link is
                    just text, so the stable application URL has to be spelled out or the citation is
                    useless once the page leaves the browser.
                  -->
                  @if (printableUrl(citation)) {
                    <span class="report-sources__url">{{ printableUrl(citation) }}</span>
                  }
                </li>
              }
            </ol>
          </section>
        }

        @if (rejections.length > 0 || unknownCitationIds.length > 0) {
          <!--
            Refused and unresolvable evidence is surfaced, not swallowed. The count is deliberately
            vague about causes: a reader needs to know that some evidence could not be restored, not
            to read a validator's reasons. The details go to the log for a developer.
          -->
          <p class="report-sources__note" role="note">
            <mat-icon aria-hidden="true">info_outline</mat-icon>
            <span>
              {{ rejections.length + unknownCitationIds.length }} source{{
                rejections.length + unknownCitationIds.length === 1 ? '' : 's'
              }}
              referenced by this report could not be verified, so no link was created for
              {{ rejections.length + unknownCitationIds.length === 1 ? 'it' : 'them' }}. The statement above is
              unchanged.
            </span>
          </p>
        }
      </mat-card-content>
    </mat-card>
  </div>
}
